import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Shield, Eye, Lock, Database, Share2, Settings, Mail } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import { generateBreadcrumbSchema, SEO_CONFIG } from '../utils/seo';

const PrivacyPolicyPage = () => {
  const { t } = useTranslation();
  
  // Breadcrumb schema for search results
  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'Privacy Policy' }
  ]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="privacy-page">
      <SEOHead 
        title="Privacy Policy | TradeHub24"
        description="TradeHub24's privacy policy explains how we collect, use, and protect your personal data on our virtual tradeshow platform."
        canonicalUrl={`${SEO_CONFIG.siteUrl}/privacy-policy`}
        structuredData={breadcrumbSchema}
      />
      
      {/* Hero Section */}
      <section className="bg-primary text-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center text-white/80 hover:text-white mb-4 transition-colors text-sm">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('legal.back_to_home')}
          </Link>
          <div className="flex items-center gap-3">
            <Shield className="h-10 w-10" />
            <h1 className="text-3xl sm:text-4xl font-bold">{t('legal.privacy.title')}</h1>
          </div>
          <p className="mt-4 text-blue-100">{t('legal.last_updated')}</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-8 space-y-8">
            
            {/* Introduction */}
            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-4">{t('legal.privacy.intro_title')}</h2>
              <p className="text-slate-600 leading-relaxed">
                {t('legal.privacy.intro_text')}
              </p>
            </div>

            {/* Information We Collect */}
            <div className="border-t border-slate-200 pt-8">
              <div className="flex items-center gap-2 mb-4">
                <Database className="h-5 w-5 text-primary" />
                <h2 className="text-xl font-bold text-slate-900">{t('legal.privacy.collect_title')}</h2>
              </div>
              <p className="text-slate-600 mb-4">{t('legal.privacy.collect_text')}</p>
              <ul className="list-disc list-inside space-y-2 text-slate-600">
                <li><strong>{t('legal.privacy.collect_account').split(':')[0]}:</strong> {t('legal.privacy.collect_account').split(':')[1]}</li>
                <li><strong>{t('legal.privacy.collect_company').split(':')[0]}:</strong> {t('legal.privacy.collect_company').split(':')[1]}</li>
                <li><strong>{t('legal.privacy.collect_contact').split(':')[0]}:</strong> {t('legal.privacy.collect_contact').split(':')[1]}</li>
                <li><strong>{t('legal.privacy.collect_booth').split(':')[0]}:</strong> {t('legal.privacy.collect_booth').split(':')[1]}</li>
                <li><strong>{t('legal.privacy.collect_technical').split(':')[0]}:</strong> {t('legal.privacy.collect_technical').split(':')[1]}</li>
                <li><strong>{t('legal.privacy.collect_usage').split(':')[0]}:</strong> {t('legal.privacy.collect_usage').split(':')[1]}</li>
              </ul>
            </div>

            {/* How We Use Information */}
            <div className="border-t border-slate-200 pt-8">
              <div className="flex items-center gap-2 mb-4">
                <Eye className="h-5 w-5 text-primary" />
                <h2 className="text-xl font-bold text-slate-900">{t('legal.privacy.use_title')}</h2>
              </div>
              <p className="text-slate-600 mb-4">{t('legal.privacy.use_text')}</p>
              <ul className="list-disc list-inside space-y-2 text-slate-600">
                <li>{t('legal.privacy.use_1')}</li>
                <li>{t('legal.privacy.use_2')}</li>
                <li>{t('legal.privacy.use_3')}</li>
                <li>{t('legal.privacy.use_4')}</li>
                <li>{t('legal.privacy.use_5')}</li>
                <li>{t('legal.privacy.use_6')}</li>
              </ul>
            </div>

            {/* Information Sharing */}
            <div className="border-t border-slate-200 pt-8">
              <div className="flex items-center gap-2 mb-4">
                <Share2 className="h-5 w-5 text-primary" />
                <h2 className="text-xl font-bold text-slate-900">{t('legal.privacy.share_title')}</h2>
              </div>
              <p className="text-slate-600 mb-4">{t('legal.privacy.share_text')}</p>
              <ul className="list-disc list-inside space-y-2 text-slate-600">
                <li>{t('legal.privacy.share_1')}</li>
                <li>{t('legal.privacy.share_2')}</li>
                <li>{t('legal.privacy.share_3')}</li>
                <li>{t('legal.privacy.share_4')}</li>
              </ul>
            </div>

            {/* Data Security */}
            <div className="border-t border-slate-200 pt-8">
              <div className="flex items-center gap-2 mb-4">
                <Lock className="h-5 w-5 text-primary" />
                <h2 className="text-xl font-bold text-slate-900">{t('legal.privacy.security_title')}</h2>
              </div>
              <p className="text-slate-600 leading-relaxed">
                {t('legal.privacy.security_text')}
              </p>
            </div>

            {/* Your Rights */}
            <div className="border-t border-slate-200 pt-8">
              <div className="flex items-center gap-2 mb-4">
                <Settings className="h-5 w-5 text-primary" />
                <h2 className="text-xl font-bold text-slate-900">{t('legal.privacy.rights_title')}</h2>
              </div>
              <p className="text-slate-600 mb-4">{t('legal.privacy.rights_text')}</p>
              <ul className="list-disc list-inside space-y-2 text-slate-600">
                <li>{t('legal.privacy.rights_1')}</li>
                <li>{t('legal.privacy.rights_2')}</li>
                <li>{t('legal.privacy.rights_3')}</li>
                <li>{t('legal.privacy.rights_4')}</li>
                <li>{t('legal.privacy.rights_5')}</li>
              </ul>
            </div>

            {/* Cookies */}
            <div className="border-t border-slate-200 pt-8">
              <h2 className="text-xl font-bold text-slate-900 mb-4">{t('legal.privacy.cookies_title')}</h2>
              <p className="text-slate-600 leading-relaxed">
                {t('legal.privacy.cookies_text')}
              </p>
            </div>

            {/* Contact Us */}
            <div className="border-t border-slate-200 pt-8">
              <div className="flex items-center gap-2 mb-4">
                <Mail className="h-5 w-5 text-primary" />
                <h2 className="text-xl font-bold text-slate-900">{t('legal.privacy.contact_title')}</h2>
              </div>
              <p className="text-slate-600 leading-relaxed">
                {t('legal.privacy.contact_text')}
              </p>
              <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                <p className="text-slate-700"><strong>Email:</strong> privacy@tradehub24.com</p>
                <p className="text-slate-700 mt-2"><strong>Phone:</strong> +1 (980) 613-4193 (USA) | +33-(0) (Europe)</p>
                <p className="text-slate-700 mt-2"><strong>Address:</strong> 10926 David Taylor Drive, Suite 120, Charlotte, NC 28262, USA</p>
              </div>
            </div>

            {/* Changes to Policy */}
            <div className="border-t border-slate-200 pt-8">
              <h2 className="text-xl font-bold text-slate-900 mb-4">{t('legal.privacy.changes_title')}</h2>
              <p className="text-slate-600 leading-relaxed">
                {t('legal.privacy.changes_text')}
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicyPage;
